import type { ElementType, ForwardedRef } from 'react';
import { forwardRef } from 'react';

import { useBumblebeeContext } from '@shieldpay/theme-provider-ui';
import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import type {
  ButtonOrLinkComponentProps,
  LinkOrButton,
} from '../../styles/clickable-element/clickable-element';
import { getClickableElementStyles } from '../../styles/clickable-element/clickable-element';
import { Box } from '../box/box';
import { ScreenReaderText } from '../screen-reader-text/screen-reader-text';
import type { TextProps } from '../text/text';
import { Text } from '../text/text';

export type ToOrHref =
  | {
      /**
       * the to prop is used when we want the link to work as a router link
       */
      to: string;
      href?: never;
      target?: never;
      preventScrollReset?: boolean;
    }
  | {
      /**
       * the href prop is used when we want the link to work outside our app
       */
      href: string;
      to?: never;
      target?: '_blank';
      preventScrollReset?: never;
    };

export type LinkProps = Pick<TextProps, 'children' | 'className'> &
  ToOrHref &
  ButtonOrLinkComponentProps & {
    'data-testid'?: string;
  };

export const TEST_IDS = {
  LINK: 'link-component',
  LEFT_ICON: 'left-icon',
  RIGHT_ICON: 'right-icon',
};

const isValidIconButton = (
  variant: ButtonOrLinkComponentProps['variant'],
  Icon: ElementType | undefined,
): Icon is ElementType => variant === 'icon';

export const LinkWithoutForwardRef = (
  {
    children,
    disabled = false,
    size = 'inherit',
    LeftIcon,
    RightIcon,
    Icon,
    iconColor,
    variant = LeftIcon || RightIcon || Icon ? 'text-link' : 'text-underline',
    to,
    className,
    preventScrollReset,
    ...props
  }: LinkProps,
  ref: ForwardedRef<unknown>,
) => {
  const { NavLink } = useBumblebeeContext();

  const {
    styles,
    iconSize,
    textVariant,
    paddingX,
    paddingY,
    spacing,
    resetStyles,
  } = getClickableElementStyles({
    ...({ size, variant } as LinkOrButton),
    disabled,
    hasLeftIcon: !!LeftIcon,
    hasRightIcon: !!RightIcon,
  });

  return (
    <Box
      disabled={disabled}
      Component={disabled ? 'span' : to ? NavLink : 'a'}
      variant="initial" // css display property set by getClickableElementStyles
      rounded
      padding={paddingX}
      to={disabled ? undefined : to}
      data-testid={TEST_IDS.LINK}
      ref={ref}
      target={to ? undefined : '_blank'}
      spacing={spacing}
      {...(to && !disabled ? { preventScrollReset } : {})} // if we pass `preventScrollReset` to a span we get a React warning - this is a workaround
      {...props}
      className={classFilter([resetStyles, styles, className])}
    >
      {LeftIcon ? (
        <LeftIcon size={iconSize} data-testid={TEST_IDS.LEFT_ICON} />
      ) : null}

      {isValidIconButton(variant, Icon) ? (
        <Box padding={paddingY}>
          <ScreenReaderText>{children}</ScreenReaderText>
          <Icon size={iconSize} color={iconColor} />
        </Box>
      ) : (
        <Text color="currentColor" padding={paddingY} variant={textVariant}>
          {children}
        </Text>
      )}

      {RightIcon ? (
        <RightIcon size={iconSize} data-testid={TEST_IDS.RIGHT_ICON} />
      ) : null}
    </Box>
  );
};

// Manually reset the displayName in the component tree.
LinkWithoutForwardRef.displayName = 'Link';

export const Link = forwardRef(LinkWithoutForwardRef);
